import { motion } from "framer-motion";
import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import s from "./Collapsible.module.scss";

export type CollapsibleProps = {
  isOpen: boolean;
  style?: CSSProperties;
  baseDuration?: number; // in ms
  withDynamicDuration?: boolean;
  skipInitialOpenAnimation?: boolean;
};

export default function Collapsible({
  isOpen,
  style,
  children,
  baseDuration = 350,
  withDynamicDuration,
  skipInitialOpenAnimation,
}: React.PropsWithChildren<CollapsibleProps>) {
  const contentRef = useRef<HTMLDivElement>(null);
  const isInitialRender = useRef(true);

  const [contentHeight, setContentHeight] = useState<number>(0);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const update = () => setContentHeight(el.scrollHeight);

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    isInitialRender.current = false;
  }, []);

  const durationMs = useMemo(() => {
    if (skipInitialOpenAnimation && isInitialRender.current && isOpen) return 0;

    if (!withDynamicDuration) return baseDuration;

    const maxDuration = baseDuration * (isOpen ? 4 : 1);
    const bySize = Math.max(contentHeight / 2, baseDuration);
    return Math.min(bySize, maxDuration);
  }, [
    skipInitialOpenAnimation,
    withDynamicDuration,
    baseDuration,
    isOpen,
    contentHeight,
  ]);

  return (
    <motion.div
      className={s.Content}
      initial={false}
      animate={{
        height: isOpen ? "auto" : 0,
      }}
      transition={{
        duration: durationMs / 1000,
        ease: "easeInOut",
      }}
      style={{
        overflow: "hidden",
      }}
    >
      <div ref={contentRef} style={style}>
        {children}
      </div>
    </motion.div>
  );
}
